import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingDisplayType,
    DrawingOptions,
    DrawingTypes,
    LineTypes,
    Point,
    PointCoordinates,
} from '../drawing.interface'
import { PrimitiveHoveredItem, Time, AutoscaleInfo, Logical, UTCTimestamp } from 'lightweight-charts'
import {
    buildAutoscaleInfoFromPrices,
    getDistanceFromSegment,
    isSegmentIntersecting,
    isTimeRangeOverlappingVisibleLogicalRange,
    scaledCoordinate,
} from '../drawing.helper'
import {
    chartLineStyleToLineType,
    CP_CANDLESTICK_MARKER_COLOR,
    CP_CANDLESTICK_BEARISH_MARKER_COLOR,
    CP_CANDLESTICK_CANDLE_FILL_OPACITY,
    CP_DEFAULT_BACKGROUND_COLOR,
    CP_DEFAULT_NECK_LINE_COLOR,
    CP_DEFAULT_PANE_LABEL_COLOR,
    CP_DEFAULT_PANE_LABEL_TEXT_COLOR,
    CP_DEFAULT_PRICE_LINE_COLOR,
    CP_DEFAULT_PRICE_LINE_WIDTH,
    resolveChartPatternOverlaySettings,
    getPatternDrawingSettingsMap,
} from './chartPatternOverlay.constants'
import { shouldDrawPriceTarget } from '../../../../util/IndicatorUtils/patterns/patternUtils'
import {
    ChartPattern,
    ChartPatternLabel,
    ChartPatternLine,
    ChartPatternLineStyle,
    ChartPatternPriceLineSegment,
    ChartPatternTargetStatus,
    getChartPatternTargetStatus,
    isCandlestickChartPattern,
} from '../../../../util/IndicatorUtils/patterns/chartPatternTypes'
import { ChartEvents } from '../../chart.events'
import {
    drawPatternTargetBadge,
    PatternTargetBadgeLayout,
} from '../patternTargetBadge'
import { drawTradingViewPatternLabel, measureTradingViewPatternLabelBounds } from '../tradingViewPatternLabel'
import {
    drawTradingViewCandlestickPatternLabel,
    measureTradingViewCandlestickPatternLabelBounds,
} from '../tradingViewCandlestickPatternLabel'

export type ChartPatternOverlaySettings = {
    fillColor?: ColorValue
    lineColor?: ColorValue
    lineWidth?: number
    priceLineColor?: ColorValue
    priceLineStyle?: LineTypes
    priceLineWidth?: number
    neckLineColor?: ColorValue
    neckLineStyle?: LineTypes
    neckLineWidth?: number
    showBackground?: boolean
    backgroundColor?: ColorValue
    showPaneLabels?: boolean
    paneLabelColor?: ColorValue
    paneLabelTextColor?: ColorValue
    showLines?: boolean
    showPolylines?: boolean
    showTrendlines?: boolean
    showPatternLines?: boolean
    showPriceTarget?: boolean
    labelColorBullish?: string
    labelColorBearish?: string
    candleFillOpacity?: number
    patterns?: ChartPattern[]
    patternTitle?: string
    seriesGroupId?: string
    priceTargetsFilter?: string
    indicator?: boolean
}

type PatternGeometry = {
    scaledCoordinates: Record<string, PointCoordinates>
}

type TargetHitArea = {
    bounds: { left: number; right: number; top: number; bottom: number }
    pattern: ChartPattern
    targetPrice: number
    tooltipAnchorX: number
    tooltipAnchorY: number
}

type MarkerHitArea = {
    bounds: { left: number; right: number; top: number; bottom: number }
    pattern: ChartPattern
    anchorPrice: number
    tooltipAnchorOffsetY: number
}

type LabelHitArea = {
    bounds: { left: number; right: number; top: number; bottom: number }
}

type LineHitArea = {
    p1: PointCoordinates
    p2: PointCoordinates
}

const CANDLESTICK_MARKER_MARGIN = 10

const parseBackgroundColorValue = (value: unknown): ColorValue => {
    if (value && typeof value === 'object' && 'color' in (value as ColorValue)) {
        return value as ColorValue
    }
    const hex = String(value || CP_DEFAULT_BACKGROUND_COLOR)
    if (/^#[0-9a-fA-F]{8}$/.test(hex)) {
        return { color: hex.slice(0, 7), opacity: parseInt(hex.slice(7, 9), 16) / 255 }
    }
    return { color: hex, opacity: 0.2 }
}

const TARGET_COLOR_AWAITING = '#9C27B0'
const TARGET_COLOR_REACHED = '#F97316'
const TV_DOUBLE_BOTTOM_TARGET_COLOR_AWAITING = '#089981'
const TV_DOUBLE_TOP_TARGET_COLOR_AWAITING = '#F23645'

/** Bars after breakout / pattern end where target line and badge anchor when not extending to last bar */
const PATTERN_LINE_END_BAR_OFFSET = 8

class ChartPatternOverlayTool extends DrawingBase<any, ChartPatternOverlaySettings, Record<string, never>> {
    private targetHitAreas: TargetHitArea[] = []
    private markerHitAreas: MarkerHitArea[] = []
    private labelHitAreas: LabelHitArea[] = []
    private lineHitAreas: LineHitArea[] = []
    private _targetHovered = false
    private _markerHovered = false
    private _hoveredTargetKey = ''
    private _hoveredMarkerKey = ''

    constructor(options: DrawingOptions<any, ChartPatternOverlaySettings, Record<string, never>>) {
        super(options)
        if (this.displayType === DrawingDisplayType.INDICATOR) {
            this.mode = undefined
            this._active = false
        }
    }

    private recordLineHit(p1: PointCoordinates, p2: PointCoordinates) {
        this.lineHitAreas.push({ p1, p2 })
    }

    private recordPatternLabelHit(
        scaledX: number,
        scaledAnchorY: number,
        text: string,
        style: 'label_up' | 'label_down',
        options: { fontSize?: number; bold?: boolean },
    ) {
        this.labelHitAreas.push({
            bounds: measureTradingViewPatternLabelBounds(
                scaledX,
                scaledAnchorY,
                text,
                style,
                {
                    fontSize: options.fontSize,
                    bold: options.bold,
                    horizontalPixelRatio: this.horizontalPixelRatio,
                    verticalPixelRatio: this.verticalPixelRatio,
                },
            ),
        })
    }

    private isPointInBounds(
        scaledX: number,
        scaledY: number,
        bounds: { left: number; right: number; top: number; bottom: number },
        margin: number,
    ) {
        return (
            scaledX >= bounds.left - margin &&
            scaledX <= bounds.right + margin &&
            scaledY >= bounds.top - margin &&
            scaledY <= bounds.bottom + margin
        )
    }

    private isPointOnPatternDrawing(x: number, y: number): boolean {
        if (!Number.isFinite(x) || !Number.isFinite(y)) return false

        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const margin = 6 * window.devicePixelRatio
        const lineThreshold = 8 * window.devicePixelRatio

        for (let i = 0; i < this.markerHitAreas.length; i++) {
            if (this.isPointInBounds(scaledX, scaledY, this.markerHitAreas[i].bounds, margin)) return true
        }
        for (let i = 0; i < this.labelHitAreas.length; i++) {
            if (this.isPointInBounds(scaledX, scaledY, this.labelHitAreas[i].bounds, margin)) return true
        }
        for (let i = 0; i < this.targetHitAreas.length; i++) {
            if (this.isPointInBounds(scaledX, scaledY, this.targetHitAreas[i].bounds, margin)) return true
        }
        for (let i = 0; i < this.lineHitAreas.length; i++) {
            const { p1, p2 } = this.lineHitAreas[i]
            if (getDistanceFromSegment(scaledX, scaledY, p1.x, p1.y, p2.x, p2.y) <= lineThreshold) return true
        }
        return false
    }

    private getResolvedStyle() {
        const flat = getPatternDrawingSettingsMap(this._settings as Record<string, unknown>)
        const resolved = resolveChartPatternOverlaySettings(flat)
        const toColor = (hex: string): ColorValue => ({ color: hex, opacity: 1 })
        return {
            ...resolved,
            priceLineColor: this._settings.priceLineColor ?? toColor(resolved.priceLineColor || CP_DEFAULT_PRICE_LINE_COLOR),
            neckLineColor: this._settings.neckLineColor ?? toColor(resolved.neckLineColor || CP_DEFAULT_NECK_LINE_COLOR),
            backgroundColor: parseBackgroundColorValue(
                flat.backgroundColor ?? flat.fillColor ?? resolved.backgroundColor ?? CP_DEFAULT_BACKGROUND_COLOR,
            ),
            paneLabelColor: this._settings.paneLabelColor ?? toColor(resolved.paneLabelColor || CP_DEFAULT_PANE_LABEL_COLOR),
            paneLabelTextColor: this._settings.paneLabelTextColor ?? toColor(resolved.paneLabelTextColor || CP_DEFAULT_PANE_LABEL_TEXT_COLOR),
            priceLineStyle: this._settings.priceLineStyle ?? chartLineStyleToLineType(resolved.priceLineStyle),
            neckLineStyle: this._settings.neckLineStyle ?? chartLineStyleToLineType(resolved.neckLineStyle),
            priceLineWidth: this._settings.priceLineWidth ?? resolved.priceLineWidth ?? CP_DEFAULT_PRICE_LINE_WIDTH,
            neckLineWidth: this._settings.neckLineWidth ?? resolved.neckLineWidth ?? CP_DEFAULT_PRICE_LINE_WIDTH,
            showBackground: resolved.showBackground,
            labelColorBullish: resolved.labelColorBullish,
            labelColorBearish: resolved.labelColorBearish,
            candleFillOpacity: Number(flat.candleFillOpacity ?? resolved.candleFillOpacity),
        }
    }

    private getCandlestickPatterns(): ChartPattern[] {
        return (this._settings.patterns ?? []).filter(
            (pattern) => isCandlestickChartPattern(pattern) && pattern.points?.length >= 1 && pattern.markerLetter,
        )
    }

    private getChartPatterns(): ChartPattern[] {
        const valid = (this._settings.patterns ?? []).filter(
            (pattern) => !isCandlestickChartPattern(pattern) && pattern.points?.length >= 2,
        )
        if (!valid.length) return []

        const latest = [...valid].sort((a, b) => {
            const aTime = (a.points[a.points.length - 1]?.x as number) ?? 0
            const bTime = (b.points[b.points.length - 1]?.x as number) ?? 0
            return bTime - aTime
        })[0]

        return latest ? [latest] : []
    }

    private computeGeometry(points: Point[]): PatternGeometry | null {
        const scaledCoordinates: Record<string, PointCoordinates> = {}
        points.forEach((point) => {
            const x = this.timeToCoordinate(point.x)
            const y = this.priceToCoordinate(point.y)
            if (x == null || y == null) return
            scaledCoordinates[point.id] = {
                x: scaledCoordinate(x, this.horizontalPixelRatio),
                y: scaledCoordinate(y, this.verticalPixelRatio),
            }
        })
        if (!Object.keys(scaledCoordinates).length) return null
        return { scaledCoordinates }
    }

    private isPennantPattern(pattern: ChartPattern) {
        return pattern.kind === 'BEARISH_PENNANT' || pattern.kind === 'BULLISH_PENNANT'
    }

    private isHeadAndShouldersPattern(pattern: ChartPattern) {
        return pattern.kind === 'HEAD_AND_SHOULDERS'
    }

    private isWedgePattern(pattern: ChartPattern) {
        return pattern.kind === 'RISING_WEDGE' || pattern.kind === 'FALLING_WEDGE'
    }

    private isTrianglePattern(pattern: ChartPattern) {
        return pattern.kind === 'ASCENDING_TRIANGLE'
            || pattern.kind === 'DESCENDING_TRIANGLE'
    }

    private isDoubleTopBottomPattern(pattern: ChartPattern) {
        return pattern.kind === 'DOUBLE_TOP' || pattern.kind === 'DOUBLE_BOTTOM'
    }

    private resolvePatternLabelColor(
        pattern: ChartPattern,
        labelDef: ChartPatternLabel,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ): string {
        return labelDef.color
            ?? pattern.priceLineSegments?.find((segment) => segment.from === labelDef.pointId || segment.to === labelDef.pointId)?.color
            ?? pattern.fillBackgroundColor
            ?? pattern.boundaryLines?.[0]?.color
            ?? style.paneLabelColor.color
    }

    private resolvePatternLabelPosition(
        pattern: ChartPattern,
        labelDef: ChartPatternLabel,
        patternPoint: Point,
    ): 'above' | 'below' {
        if (labelDef.position) return labelDef.position

        const id = labelDef.pointId.toUpperCase()
        if (/^H\d/.test(id) || id === 'R' || id === 'UA' || id === 'UB') return 'above'
        if (/^L\d/.test(id) || id === 'S' || id === 'LA' || id === 'LB') return 'below'
        if (pattern.kind === 'DOUBLE_TOP' && (id === '2' || id === '4')) return 'above'
        if (pattern.kind === 'DOUBLE_BOTTOM' && (id === '2' || id === '4')) return 'below'

        const candle = this.findCandleByTime(patternPoint.x)
        if (candle) {
            const mid = ((candle.high as number) + (candle.low as number)) / 2
            return patternPoint.y >= mid ? 'above' : 'below'
        }
        return pattern.bias === 'bullish' ? 'below' : 'above'
    }

    private getScaledXForTime(time: Time): number | null {
        const x = this.timeToCoordinate(time)
        if (x == null) return null
        return scaledCoordinate(x, this.horizontalPixelRatio)
    }

    private getTimeAtBarOffset(fromTime: number, barOffset: number): number | null {
        const data = this._chart.getRenderedPrimaryData()
        const idx = data.findIndex((candle) => (candle.time as number) === fromTime)
        if (idx < 0) return null
        const targetIdx = Math.min(data.length - 1, Math.max(0, idx + barOffset))
        return data[targetIdx]?.time as number ?? null
    }

    private getPatternTargetAnchorTime(pattern: ChartPattern): number | null {
        const anchorId = pattern.targetAnchorPointId ?? pattern.points[pattern.points.length - 1]?.id
        const anchor = pattern.points.find((p) => p.id === anchorId)
        if (!anchor) return null
        return anchor.x as number
    }

    private getPatternRightmostTime(pattern: ChartPattern): number {
        return pattern.points.reduce((max, point) => {
            const time = point.x as number
            return Number.isFinite(time) && time > max ? time : max
        }, 0)
    }

    private getBarsFromLastBar(anchorTime: number): number | null {
        const data = this._chart.getRenderedPrimaryData()
        if (!data.length) return null
        const lastIdx = data.length - 1
        const anchorIdx = data.findIndex((candle) => (candle.time as number) === anchorTime)
        if (anchorIdx < 0) return null
        return lastIdx - anchorIdx
    }

    private isBearishPattern(pattern: ChartPattern): boolean {
        return pattern.bias === 'bearish'
            || pattern.kind === 'DOUBLE_TOP'
            || pattern.kind === 'DESCENDING_TRIANGLE'
            || pattern.kind === 'RISING_WEDGE'
    }

    private getTargetReachedBarTime(pattern: ChartPattern, targetPrice: number): number | null {
        const anchorTime = this.getPatternTargetAnchorTime(pattern)
        if (anchorTime == null) return null
        const bearish = this.isBearishPattern(pattern)
        const data = this._chart.getRenderedPrimaryData()
        for (let i = 0; i < data.length; i++) {
            const time = data[i].time as number
            if (time < anchorTime) continue
            const low = data[i].low as number
            const high = data[i].high as number
            if (bearish ? low <= targetPrice : high >= targetPrice) {
                return time
            }
        }
        return null
    }

    /** Extend lines to the chart edge only for in-progress patterns whose breakout is near the last bar */
    private shouldExtendPatternToLastBar(pattern: ChartPattern): boolean {
        if (!pattern.inProgress) return false
        const anchorTime = this.getPatternTargetAnchorTime(pattern)
        if (anchorTime == null) return false
        const barsFromEnd = this.getBarsFromLastBar(anchorTime)
        return barsFromEnd != null && barsFromEnd <= 24
    }

    private getPatternLineEndTime(pattern: ChartPattern, targetPrice?: number): number | null {
        if (targetPrice != null) {
            const status = this.getPatternStatusForTarget(pattern, targetPrice)
            if (status === 'Reached') {
                const reachedTime = this.getTargetReachedBarTime(pattern, targetPrice)
                if (reachedTime != null) return reachedTime
            }
        }

        if (this.shouldExtendPatternToLastBar(pattern)) {
            const data = this._chart.getRenderedPrimaryData()
            const lastTime = data[data.length - 1]?.time
            return lastTime != null ? lastTime as number : null
        }

        const baseTime = this.getPatternTargetAnchorTime(pattern) ?? this.getPatternRightmostTime(pattern)
        if (!baseTime) return null
        return this.getTimeAtBarOffset(baseTime, PATTERN_LINE_END_BAR_OFFSET)
    }

    private getPatternTargetEndScaledX(
        pattern: ChartPattern,
        minX: number,
        targetPrice?: number,
    ): number {
        const endTime = this.getPatternLineEndTime(pattern, targetPrice)
        if (endTime != null) {
            const scaledX = this.getScaledXForTime(endTime as UTCTimestamp)
            if (scaledX != null) return Math.max(scaledX, minX)
        }
        return minX
    }

    private drawDoubleTopBottomPriceLines(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        segments: ChartPatternPriceLineSegment[],
        coords: Record<string, PointCoordinates>,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ) {
        const c1 = coords['1']
        const c2 = coords['2']
        const c3 = coords['3']
        const neckY = c3?.y
        const leftNeck = c1 && c2 && neckY != null ? this.getFirstLegNecklineLeft(c1, c2, neckY) : null

        segments.forEach((segment, index) => {
            let p1 = coords[segment.from]
            const p2 = coords[segment.to]
            if (!p1 || !p2) return
            if (index === 0 && leftNeck && segment.from === '1' && segment.to === '2' && leftNeck.x < p1.x) {
                p1 = leftNeck
            }
            const lineType = this.toLineType(segment.style ?? 'solid')
            const segmentColor = segment.color
                ? { color: segment.color, opacity: style.priceLineColor.opacity }
                : style.priceLineColor
            ctx.beginPath()
            ctx.moveTo(p1.x, p1.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.globalAlpha = segmentColor.opacity
            this.setLineAttributes(ctx, true, segmentColor, segment.width ?? style.priceLineWidth, lineType)
            ctx.stroke()
            ctx.closePath()
            this.recordLineHit(p1, p2)
        })
    }

    private getDoubleTopBottomPriceLineSegments(pattern: ChartPattern): ChartPatternPriceLineSegment[] {
        if (pattern.priceLineSegments?.length) return pattern.priceLineSegments
        const ids = pattern.polylineIds ?? pattern.points.map((p) => p.id)
        const color = pattern.fillBackgroundColor ?? pattern.priceLineSegments?.[0]?.color
        return ids.slice(0, -1).map((from, index) => ({
            from,
            to: ids[index + 1],
            style: 'solid' as const,
            color,
        }))
    }

    private drawPriceLineSegments(
        ctx: CanvasRenderingContext2D,
        segments: ChartPatternPriceLineSegment[],
        coords: Record<string, PointCoordinates>,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ) {
        segments.forEach((segment) => {
            const p1 = coords[segment.from]
            const p2 = coords[segment.to]
            if (!p1 || !p2) return
            const lineType = this.toLineType(segment.style ?? 'solid')
            const segmentColor = segment.color
                ? { color: segment.color, opacity: style.priceLineColor.opacity }
                : style.priceLineColor
            ctx.beginPath()
            ctx.moveTo(p1.x, p1.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.globalAlpha = segmentColor.opacity
            this.setLineAttributes(ctx, true, segmentColor, segment.width ?? style.priceLineWidth, lineType)
            ctx.stroke()
            ctx.closePath()
            this.recordLineHit(p1, p2)
        })
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        const style = this.getResolvedStyle()
        this.targetHitAreas = []
        this.markerHitAreas = []
        this.labelHitAreas = []
        this.lineHitAreas = []

        this.getCandlestickPatterns().forEach((pattern) => {
            this.drawCandlestickMarker(ctx, pattern, scope, style)
        })

        this.getChartPatterns().forEach((pattern) => {
            const geometry = this.computeGeometry(pattern.points)
            if (!geometry) return
            this.drawPattern(ctx, pattern, geometry, style)
        })
    }

    /** Full bar slot from previous/next candle midpoints — matches volume histogram width */
    private getCandleHighlightBounds(
        time: Time,
        scope: BitmapCoordinatesRenderingScope,
    ): { left: number; right: number } | null {
        const hpr = scope.horizontalPixelRatio
        const centerX = this.timeToCoordinate(time)
        if (centerX == null) return null

        const data = this._chart.getRenderedPrimaryData()
        const anchorTime = time as number
        const idx = data.findIndex((candle) => (candle.time as number) === anchorTime)

        const barSpacing = (this._chart.getBarSpace() ?? 6)

        if (idx >= 0) {
            const prevTime = idx > 0 ? data[idx - 1]?.time : undefined
            const nextTime = idx < data.length - 1 ? data[idx + 1]?.time : undefined
            const prevCoord = prevTime != null ? this.timeToCoordinate(prevTime) : null
            const nextCoord = nextTime != null ? this.timeToCoordinate(nextTime) : null

            let leftX: number
            let rightX: number

            if (prevCoord != null && nextCoord != null) {
                leftX = (prevCoord + centerX) / 2
                rightX = (centerX + nextCoord) / 2
            } else if (prevCoord != null) {
                const span = centerX - prevCoord
                leftX = centerX - span / 2
                rightX = centerX + span / 2
            } else if (nextCoord != null) {
                const span = nextCoord - centerX
                leftX = centerX - span / 2
                rightX = centerX + span / 2
            } else {
                leftX = centerX - barSpacing / 2
                rightX = centerX + barSpacing / 2
            }

            return {
                left: scaledCoordinate(leftX, hpr),
                right: scaledCoordinate(rightX, hpr),
            }
        }

        const half = (barSpacing * hpr) / 2
        const scaledCenter = scaledCoordinate(centerX, hpr)
        return { left: scaledCenter - half, right: scaledCenter + half }
    }

    private drawCandleHighlight(
        ctx: CanvasRenderingContext2D,
        left: number,
        right: number,
        paneHeight: number,
        color: string,
        opacity: number,
    ) {
        if (paneHeight <= 0 || right <= left) return

        ctx.save()
        ctx.fillStyle = color
        ctx.globalAlpha = opacity
        ctx.fillRect(left, 0, right - left, paneHeight)
        ctx.restore()
    }

    private drawCandlestickMarker(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        scope: BitmapCoordinatesRenderingScope,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ) {
        const anchor = pattern.points[0]
        const letter = pattern.markerLetter ?? ''
        if (!anchor || !letter) return

        const x = this.timeToCoordinate(anchor.x)
        const closePrice = pattern.candleClose ?? anchor.y
        const lowPrice = pattern.candleLow ?? closePrice
        const highPrice = pattern.candleHigh ?? closePrice
        const lowY = this.priceToCoordinate(lowPrice)
        const highY = this.priceToCoordinate(highPrice)
        if (x == null || lowY == null || highY == null) return

        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledLowY = scaledCoordinate(lowY, this.verticalPixelRatio)
        const scaledHighY = scaledCoordinate(highY, this.verticalPixelRatio)
        const paneHeight = scope.mediaSize.height * scope.verticalPixelRatio

        const markerColor = pattern.bias === 'bearish'
            ? (style.labelColorBearish ?? CP_CANDLESTICK_BEARISH_MARKER_COLOR)
            : (style.labelColorBullish ?? CP_CANDLESTICK_MARKER_COLOR)
        const isBullish = pattern.bias !== 'bearish'
        const margin = CANDLESTICK_MARKER_MARGIN * scope.verticalPixelRatio
        const markerH = 14 * scope.verticalPixelRatio
        const pointerH = 6 * scope.verticalPixelRatio
        const labelOptions = {
            horizontalPixelRatio: this.horizontalPixelRatio,
            verticalPixelRatio: this.verticalPixelRatio,
            markerHeight: 14,
            pointerHeight: 6,
            minMarkerWidth: 22,
        }

        if (style.showBackground) {
            const fillColor = typeof style.backgroundColor === 'object'
                ? style.backgroundColor.color
                : (style.backgroundColor as string | undefined) ?? markerColor
            const fillOpacity = typeof style.backgroundColor === 'object'
                ? style.backgroundColor.opacity
                : style.candleFillOpacity ?? CP_CANDLESTICK_CANDLE_FILL_OPACITY
            const highlightTimes = pattern.highlightCandleTimes?.length
                ? pattern.highlightCandleTimes
                : [anchor.x]
            highlightTimes.forEach((time) => {
                const bounds = this.getCandleHighlightBounds(time, scope)
                if (!bounds) return
                this.drawCandleHighlight(
                    ctx,
                    bounds.left,
                    bounds.right,
                    paneHeight,
                    fillColor,
                    fillOpacity,
                )
            })
        }

        let tipY: number
        let markerBaseY: number
        let tooltipAnchorY: number
        let anchorPrice: number
        let markerBounds: { left: number; right: number; top: number; bottom: number }

        if (isBullish) {
            anchorPrice = lowPrice
            tipY = scaledLowY + margin
            markerBaseY = tipY + pointerH + markerH
            tooltipAnchorY = tipY
            drawTradingViewCandlestickPatternLabel(
                ctx,
                scaledX,
                markerBaseY,
                letter,
                markerColor,
                'up',
                labelOptions,
            )
            markerBounds = measureTradingViewCandlestickPatternLabelBounds(
                scaledX,
                markerBaseY,
                letter,
                'up',
                labelOptions,
            )
        } else {
            anchorPrice = highPrice
            tipY = scaledHighY - margin
            markerBaseY = tipY - pointerH - markerH
            tooltipAnchorY = markerBaseY
            const badgeBaseY = markerBaseY + markerH
            drawTradingViewCandlestickPatternLabel(
                ctx,
                scaledX,
                badgeBaseY,
                letter,
                markerColor,
                'down',
                labelOptions,
            )
            markerBounds = measureTradingViewCandlestickPatternLabelBounds(
                scaledX,
                badgeBaseY,
                letter,
                'down',
                labelOptions,
            )
        }

        const refY = isBullish ? lowY : highY
        const tooltipAnchorOffsetY = tooltipAnchorY / scope.verticalPixelRatio - refY
        this.markerHitAreas.push({ bounds: markerBounds, pattern, anchorPrice, tooltipAnchorOffsetY })
    }

    private findCandleByTime(time: Time) {
        const anchorTime = time as number
        return this._chart.getRenderedPrimaryData().find((candle) => (candle.time as number) === anchorTime)
    }

    private getCandleExtremePrice(
        patternPoint: Point,
        position?: 'above' | 'below',
    ): number {
        const candle = this.findCandleByTime(patternPoint.x)
        if (!candle) return patternPoint.y
        if (position === 'below') return (candle.low as number) ?? patternPoint.y
        return (candle.high as number) ?? patternPoint.y
    }

    private getScaledCandleExtremeY(
        patternPoint: Point,
        position?: 'above' | 'below',
    ): number | null {
        const price = this.getCandleExtremePrice(patternPoint, position)
        const y = this.priceToCoordinate(price)
        if (y == null) return null
        return scaledCoordinate(y, this.verticalPixelRatio)
    }

    private drawPattern(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        geometry: PatternGeometry,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>
    ) {
        const coords = geometry.scaledCoordinates
        const isWedge = this.isWedgePattern(pattern)
        const isPennant = this.isPennantPattern(pattern)
        const isDoubleTopBottom = this.isDoubleTopBottomPattern(pattern)
        const showTrendlines = style.showTrendlines !== false && style.showLines !== false
        const showPatternLines = style.showPatternLines !== false && style.showPolylines !== false

        if (isDoubleTopBottom) {
            this.drawDoubleTopBottomFill(ctx, pattern, coords, style)
        }

        if (style.showBackground && pattern.fillPolygonIds?.length === 4) {
            const [id1, id2, id3, id4] = pattern.fillPolygonIds
            const p1 = coords[id1]
            const p2 = coords[id2]
            const p3 = coords[id3]
            const p4 = coords[id4]
            if (p1 && p2 && p3 && p4) {
                ctx.beginPath()
                ctx.moveTo(p1.x, p1.y)
                ctx.lineTo(p2.x, p2.y)
                ctx.lineTo(p3.x, p3.y)
                ctx.lineTo(p4.x, p4.y)
                ctx.closePath()
                ctx.globalAlpha = pattern.fillBackgroundOpacity ?? style.backgroundColor.opacity ?? 0.2
                ctx.fillStyle = pattern.fillBackgroundColor ?? style.backgroundColor.color
                ctx.fill()
            }
        } else if (style.showBackground && pattern.fillTriangles?.length && !this.isDoubleTopBottomPattern(pattern)) {
            const fillColor = pattern.fillBackgroundColor ?? style.backgroundColor.color
            const fillOpacity = pattern.fillBackgroundOpacity ?? style.backgroundColor.opacity ?? 0.2
            pattern.fillTriangles.forEach((triangle) => {
                const [id1, id2, id3] = triangle
                const p1 = coords[id1]
                const p2 = coords[id2]
                const p3 = coords[id3]
                if (!p1 || !p2 || !p3) return
                ctx.beginPath()
                ctx.moveTo(p1.x, p1.y)
                ctx.lineTo(p2.x, p2.y)
                ctx.lineTo(p3.x, p3.y)
                ctx.closePath()
                ctx.globalAlpha = fillOpacity
                ctx.fillStyle = fillColor
                ctx.fill()
            })
        }

        if (!isDoubleTopBottom && showTrendlines && pattern.boundaryLines?.length && !this.isHeadAndShouldersPattern(pattern)) {
            const isTriangle = this.isTrianglePattern(pattern)
            pattern.boundaryLines.forEach((line) => {
                const wedgeStyle = isWedge || isPennant
                    ? { ...style, neckLineColor: style.priceLineColor, neckLineStyle: style.priceLineStyle, neckLineWidth: style.priceLineWidth }
                    : style
                const lineStyle = isWedge || isPennant
                    ? 'normal'
                    : isTriangle
                        ? this.toLineType(line.style ?? 'solid')
                        : this.toLineType(line.style)
                this.drawBoundaryLine(ctx, line, coords, wedgeStyle, lineStyle)
            })
            if (!isWedge && !isPennant && !isDoubleTopBottom) {
                this.drawLineEndMarkers(ctx, pattern, coords)
            }
        }

        if (showPatternLines) {
            if (isDoubleTopBottom) {
                this.drawDoubleTopBottomPriceLines(
                    ctx,
                    pattern,
                    this.getDoubleTopBottomPriceLineSegments(pattern),
                    coords,
                    style,
                )
            } else if (pattern.priceLineSegments?.length) {
                this.drawPriceLineSegments(ctx, pattern.priceLineSegments, coords, style)
            } else {
                const polylineIds = pattern.polylineIds ?? pattern.points.map((p) => p.id)
                let prev: PointCoordinates | undefined
                ctx.beginPath()
                this.setLineAttributes(ctx, false, style.priceLineColor, style.priceLineWidth, style.priceLineStyle)
                polylineIds.forEach((id) => {
                    const point = coords[id]
                    if (!point) return
                    if (!prev) ctx.moveTo(point.x, point.y)
                    else {
                        ctx.lineTo(point.x, point.y)
                        this.recordLineHit(prev, point)
                    }
                    prev = point
                })
                if (prev) {
                    ctx.globalAlpha = style.priceLineColor.opacity
                    ctx.stroke()
                }
                ctx.closePath()
            }
        }

        if (this.isHeadAndShouldersPattern(pattern) && showTrendlines && style.showLines !== false) {
            this.drawHeadAndShouldersNeckline(ctx, pattern, coords, style)
            this.drawHeadAndShouldersSideFills(ctx, pattern, coords, style)
        }

        if (isDoubleTopBottom) {
            this.drawDoubleTopBottomNeckline(ctx, pattern, coords, style)
        }

        if (style.showPaneLabels && pattern.labels?.length) {
            pattern.labels.forEach((labelDef) => {
                const patternPoint = pattern.points.find((p) => p.id === labelDef.pointId)
                if (!patternPoint) return
                const color = this.resolvePatternLabelColor(pattern, labelDef, style)
                const position = this.resolvePatternLabelPosition(pattern, labelDef, patternPoint)
                if (this.isPennantPattern(pattern)) {
                    this.drawPennantTitleLabel(ctx, patternPoint, labelDef.text, color, position)
                    return
                }
                this.drawCalloutLabel(ctx, patternPoint, labelDef.text, color, position)
            })
        }

        if (pattern.breakoutMarker && !this.isWedgePattern(pattern)) {
            this.drawBreakoutMarker(ctx, pattern, coords, style)
        }

        if (style.showPriceTarget) {
            if (!this.isAscendingOrDescendingTriangle(pattern)) {
                this.drawTargetGuide(ctx, pattern, coords, style)
            }
            this.drawTargets(ctx, pattern, coords, style)
        }
    }

    private getHeadAndShouldersNecklineSlope(coords: Record<string, PointCoordinates>) {
        const c = coords.c
        const e = coords.e
        if (!c || !e || e.x === c.x) return null
        const slope = (e.y - c.y) / (e.x - c.x)
        return { slope, intercept: c.y - slope * c.x }
    }

    private getHeadAndShouldersNecklineIntersection(
        coords: Record<string, PointCoordinates>,
        fromId: string,
        toId: string,
        neckline: { slope: number; intercept: number },
    ): PointCoordinates | null {
        const from = coords[fromId]
        const to = coords[toId]
        if (!from || !to || to.x === from.x) return null
        const segmentSlope = (to.y - from.y) / (to.x - from.x)
        const segmentIntercept = from.y - segmentSlope * from.x
        if (Math.abs(segmentSlope - neckline.slope) < 1e-9) return null
        const x = (neckline.intercept - segmentIntercept) / (segmentSlope - neckline.slope)
        const y = neckline.slope * x + neckline.intercept
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null
        return { x, y }
    }

    private drawHeadAndShouldersNeckline(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        coords: Record<string, PointCoordinates>,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ) {
        const c = coords.c
        const e = coords.e
        const g = coords.g
        if (!c || !e) return

        const neckline = this.getHeadAndShouldersNecklineSlope(coords)
        if (!neckline) return

        const left = this.getHeadAndShouldersNecklineIntersection(coords, 'a', 'b', neckline) ?? c
        let right = this.getHeadAndShouldersNecklineIntersection(coords, 'e', 'f', neckline)
            ?? (g ?? e)

        const data = this._chart.getRenderedPrimaryData()
        if (data.length) {
            const lastTime = data[data.length - 1]?.time
            const lastX = lastTime != null ? this.timeToCoordinate(lastTime) : null
            if (lastX != null) {
                const scaledLastX = scaledCoordinate(lastX, this.horizontalPixelRatio)
                if (scaledLastX > right.x) {
                    right = {
                        x: scaledLastX,
                        y: neckline.slope * scaledLastX + neckline.intercept,
                    }
                }
            }
        }

        ctx.beginPath()
        ctx.moveTo(left.x, left.y)
        ctx.lineTo(right.x, right.y)
        ctx.globalAlpha = style.neckLineColor.opacity
        this.setLineAttributes(ctx, true, style.neckLineColor, style.neckLineWidth, style.neckLineStyle)
        ctx.stroke()
        ctx.closePath()
        this.recordLineHit(left, right)
    }

    private drawHeadAndShouldersSideFills(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        coords: Record<string, PointCoordinates>,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ) {
        if (!style.showBackground) return

        const neckline = this.getHeadAndShouldersNecklineSlope(coords)
        if (!neckline) return

        const { b, c, e, f } = coords
        if (!b || !c || !e || !f) return

        const l = this.getHeadAndShouldersNecklineIntersection(coords, 'a', 'b', neckline)
        const r = this.getHeadAndShouldersNecklineIntersection(coords, 'e', 'f', neckline)

        const fillColor = pattern.fillBackgroundColor ?? style.backgroundColor.color
        const fillOpacity = pattern.fillBackgroundOpacity ?? style.backgroundColor.opacity ?? 0.2

        const drawTriangle = (p1: PointCoordinates, p2: PointCoordinates, p3: PointCoordinates) => {
            ctx.beginPath()
            ctx.moveTo(p1.x, p1.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.lineTo(p3.x, p3.y)
            ctx.closePath()
            ctx.globalAlpha = fillOpacity
            ctx.fillStyle = fillColor
            ctx.fill()
        }

        if (l && isSegmentIntersecting(b.x, b.y, c.x, c.y, neckline.slope, neckline.intercept)) {
            drawTriangle(l, b, c)
        }
        if (r && isSegmentIntersecting(f.x, f.y, e.x, e.y, neckline.slope, neckline.intercept)) {
            drawTriangle(e, f, r)
        }
    }

    /** Intersection of horizontal neckY with segment a→b in screen space (matches drawn price lines) */
    private segmentHorizontalIntersect(
        a: PointCoordinates,
        b: PointCoordinates,
        neckY: number,
    ): PointCoordinates | null {
        if (b.y === a.y) return null
        const lo = Math.min(a.y, b.y)
        const hi = Math.max(a.y, b.y)
        if (neckY < lo || neckY > hi) return null
        const t = (neckY - a.y) / (b.y - a.y)
        return { x: a.x + t * (b.x - a.x), y: neckY }
    }

    /** Extended line a→b ∩ horizontal neckY — neckline meets first/last leg even off-segment */
    private lineHorizontalIntersect(
        a: PointCoordinates,
        b: PointCoordinates,
        neckY: number,
    ): PointCoordinates | null {
        if (b.y === a.y) return null
        const t = (neckY - a.y) / (b.y - a.y)
        return { x: a.x + t * (b.x - a.x), y: neckY }
    }

    /** Left neckline anchor on leg 1→2; extends when point 1 is on the wrong side of the neckline */
    private getFirstLegNecklineLeft(
        c1: PointCoordinates,
        c2: PointCoordinates,
        neckY: number,
    ): PointCoordinates | null {
        const hit = this.lineHorizontalIntersect(c1, c2, neckY)
        if (!hit || c2.y === c1.y) return null
        const t = (neckY - c1.y) / (c2.y - c1.y)
        if (t > 1) return null
        return hit
    }

    private findDoublePatternNecklineEnds(
        coords: Record<string, PointCoordinates>,
        neckY: number,
    ): { left: PointCoordinates; right: PointCoordinates } | null {
        const c1 = coords['1']
        const c2 = coords['2']
        const c3 = coords['3']
        const c4 = coords['4']
        const c5 = coords['5']
        if (!c1 || !c2 || !c3 || !c4 || !c5) return null

        const leftOnFirst = this.getFirstLegNecklineLeft(c1, c2, neckY)
        const rightOnLast = this.lineHorizontalIntersect(c4, c5, neckY)

        const segments: Array<[PointCoordinates, PointCoordinates]> = [
            [c1, c2],
            [c2, c3],
            [c3, c4],
            [c4, c5],
        ]
        const hits: PointCoordinates[] = []
        segments.forEach(([a, b]) => {
            const hit = this.segmentHorizontalIntersect(a, b, neckY)
            if (hit) hits.push(hit)
        })
        if (!leftOnFirst && !rightOnLast && !hits.length) return null

        hits.sort((a, b) => a.x - b.x)
        return {
            left: leftOnFirst ?? hits[0],
            right: rightOnLast ?? hits[hits.length - 1],
        }
    }

    private drawDoubleTopBottomFill(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        coords: Record<string, PointCoordinates>,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ) {
        if (style.showBackground === false) return
        const c2 = coords['2']
        const c3 = coords['3']
        const c4 = coords['4']
        if (!c2 || !c3 || !c4) return

        const ends = this.findDoublePatternNecklineEnds(coords, c3.y)
        if (!ends) return

        const fillOpacity = pattern.fillBackgroundOpacity ?? style.backgroundColor.opacity ?? 0.2
        ctx.beginPath()
        ctx.moveTo(ends.left.x, ends.left.y)
        ctx.lineTo(c2.x, c2.y)
        ctx.lineTo(c3.x, c3.y)
        ctx.lineTo(c4.x, c4.y)
        ctx.lineTo(ends.right.x, ends.right.y)
        ctx.closePath()
        ctx.globalAlpha = fillOpacity
        ctx.fillStyle = pattern.fillBackgroundColor ?? (typeof style.backgroundColor === 'object'
            ? style.backgroundColor.color
            : style.backgroundColor) ?? style.backgroundColor.color
        ctx.fill()
    }

    private drawDoubleTopBottomNeckline(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        coords: Record<string, PointCoordinates>,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ) {
        const c3 = coords['3']
        if (!c3) return

        const ends = this.findDoublePatternNecklineEnds(coords, c3.y)
        if (!ends) return

        const necklineEndX = this.getPatternTargetEndScaledX(pattern, ends.right.x)

        const lineColor = pattern.boundaryLines?.[0]?.color
            ? { color: pattern.boundaryLines[0].color!, opacity: style.neckLineColor.opacity }
            : style.neckLineColor
        ctx.beginPath()
        this.setLineAttributes(ctx, true, lineColor, style.neckLineWidth, 'dotted')
        ctx.moveTo(ends.left.x, ends.left.y)
        ctx.lineTo(necklineEndX, ends.left.y)
        ctx.stroke()
        ctx.closePath()
        this.recordLineHit(ends.left, { x: necklineEndX, y: ends.left.y })
    }

    private isAscendingOrDescendingTriangle(pattern: ChartPattern) {
        return pattern.kind === 'ASCENDING_TRIANGLE' || pattern.kind === 'DESCENDING_TRIANGLE'
    }

    private drawPennantTitleLabel(
        ctx: CanvasRenderingContext2D,
        patternPoint: Point,
        text: string,
        color: string,
        position?: 'above' | 'below',
    ) {
        const x = this.timeToCoordinate(patternPoint.x)
        if (x == null) return
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledAnchorY = this.getScaledCandleExtremeY(patternPoint, position)
        if (scaledAnchorY == null) return

        const above = position !== 'below'
        drawTradingViewPatternLabel(
            ctx,
            scaledX,
            scaledAnchorY,
            text,
            color,
            above ? 'label_down' : 'label_up',
            {
                fontSize: 11,
                bold: true,
                horizontalPixelRatio: this.horizontalPixelRatio,
                verticalPixelRatio: this.verticalPixelRatio,
            },
        )
        this.recordPatternLabelHit(
            scaledX,
            scaledAnchorY,
            text,
            above ? 'label_down' : 'label_up',
            { fontSize: 11, bold: true },
        )
    }

    private drawCalloutLabel(
        ctx: CanvasRenderingContext2D,
        patternPoint: Point,
        text: string,
        color: string,
        position?: 'above' | 'below',
    ) {
        const x = this.timeToCoordinate(patternPoint.x)
        if (x == null) return
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledAnchorY = this.getScaledCandleExtremeY(patternPoint, position)
        if (scaledAnchorY == null) return

        const above = position !== 'below'
        drawTradingViewPatternLabel(
            ctx,
            scaledX,
            scaledAnchorY,
            text,
            color,
            above ? 'label_down' : 'label_up',
            {
                fontSize: 11,
                bold: true,
                horizontalPixelRatio: this.horizontalPixelRatio,
                verticalPixelRatio: this.verticalPixelRatio,
            },
        )
        this.recordPatternLabelHit(
            scaledX,
            scaledAnchorY,
            text,
            above ? 'label_down' : 'label_up',
            { fontSize: 11, bold: true },
        )
    }

    private drawLineEndMarkers(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        coords: Record<string, PointCoordinates>,
    ) {
        if (!pattern.lineEndMarkers?.length) return
        const markerSize = 7 * window.devicePixelRatio
        const margin = CANDLESTICK_MARKER_MARGIN * this.verticalPixelRatio
        pattern.lineEndMarkers.forEach((marker) => {
            const point = coords[marker.pointId]
            const patternPoint = pattern.points.find((p) => p.id === marker.pointId)
            if (!point || !patternPoint) return
            const color = marker.color ?? '#089981'

            let centerY = point.y
            if (marker.direction === 'up') {
                const scaledLowY = this.getScaledCandleExtremeY(patternPoint, 'below')
                if (scaledLowY != null) {
                    centerY = scaledLowY + margin + markerSize
                }
            } else {
                const scaledHighY = this.getScaledCandleExtremeY(patternPoint, 'above')
                if (scaledHighY != null) {
                    centerY = scaledHighY - margin - markerSize
                }
            }

            ctx.save()
            ctx.fillStyle = color
            ctx.beginPath()
            if (marker.direction === 'up') {
                ctx.moveTo(point.x, centerY - markerSize)
                ctx.lineTo(point.x - markerSize, centerY + markerSize * 0.6)
                ctx.lineTo(point.x + markerSize, centerY + markerSize * 0.6)
            } else {
                ctx.moveTo(point.x, centerY + markerSize)
                ctx.lineTo(point.x - markerSize, centerY - markerSize * 0.6)
                ctx.lineTo(point.x + markerSize, centerY - markerSize * 0.6)
            }
            ctx.closePath()
            ctx.fill()
            ctx.restore()
        })
    }

    private drawBoundaryLine(
        ctx: CanvasRenderingContext2D,
        line: ChartPatternLine,
        coords: Record<string, PointCoordinates>,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
        lineTypeOverride?: LineTypes
    ) {
        const p1 = coords[line.from]
        const p2 = coords[line.to]
        if (!p1 || !p2) return
        const lineType = lineTypeOverride ?? this.toLineType(line.style)
        const lineColor = line.color
            ? { color: line.color, opacity: style.neckLineColor.opacity }
            : style.neckLineColor
        ctx.beginPath()
        ctx.moveTo(p1.x, p1.y)
        ctx.lineTo(p2.x, p2.y)
        ctx.globalAlpha = lineColor.opacity
        this.setLineAttributes(ctx, true, lineColor, style.neckLineWidth, lineType)
        ctx.stroke()
        ctx.closePath()
        this.recordLineHit(p1, p2)
    }

    private drawBreakoutMarker(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        coords: Record<string, PointCoordinates>,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ) {
        const marker = pattern.breakoutMarker
        if (!marker) return
        const point = coords[marker.pointId]
        const patternPoint = pattern.points.find((p) => p.id === marker.pointId)
        if (!point || !patternPoint) return

        const text = marker.text ?? '✓'
        const color = marker.color ?? style.paneLabelColor.color
        const above = marker.position !== 'below'
        const anchorY = above
            ? (this.getScaledCandleExtremeY(patternPoint, 'above') ?? point.y)
            : (this.getScaledCandleExtremeY(patternPoint, 'below') ?? point.y)

        drawTradingViewPatternLabel(
            ctx,
            point.x,
            anchorY,
            text,
            color,
            above ? 'label_down' : 'label_up',
            {
                fontSize: 12,
                bold: false,
                horizontalPixelRatio: this.horizontalPixelRatio,
                verticalPixelRatio: this.verticalPixelRatio,
            },
        )
        this.recordPatternLabelHit(
            point.x,
            anchorY,
            text,
            above ? 'label_down' : 'label_up',
            { fontSize: 12, bold: false },
        )
    }

    private drawTargetGuide(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        coords: Record<string, PointCoordinates>,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ) {
        if (!pattern.targetGuidePointId || pattern.targetGuidePrice == null) return
        const point = coords[pattern.targetGuidePointId]
        const anchorId = pattern.targetAnchorPointId ?? pattern.targetGuidePointId
        const anchor = coords[anchorId]
        const guideY = this.priceToCoordinate(pattern.targetGuidePrice)
        if (!point || !anchor || guideY == null) return

        const scaledGuideY = scaledCoordinate(guideY, this.verticalPixelRatio)
        const color = { color: pattern.targetGuideColor ?? style.neckLineColor.color, opacity: style.neckLineColor.opacity }
        this.setLineAttributes(ctx, true, color, style.neckLineWidth, 'dashed')
        ctx.beginPath()
        ctx.moveTo(anchor.x, anchor.y)
        ctx.lineTo(anchor.x, scaledGuideY)
        ctx.moveTo(point.x, scaledGuideY)
        ctx.lineTo(anchor.x, scaledGuideY)
        ctx.stroke()
        ctx.closePath()
        this.recordLineHit(anchor, { x: anchor.x, y: scaledGuideY })
        this.recordLineHit({ x: point.x, y: scaledGuideY }, { x: anchor.x, y: scaledGuideY })

        const markerSize = 9 * window.devicePixelRatio
        ctx.fillStyle = color.color
        ctx.beginPath()
        ctx.moveTo(point.x, scaledGuideY - markerSize)
        ctx.lineTo(point.x - markerSize, scaledGuideY + markerSize)
        ctx.lineTo(point.x + markerSize, scaledGuideY + markerSize)
        ctx.closePath()
        ctx.fill()
    }

    private toLineType(style?: ChartPatternLineStyle): LineTypes {
        if (style === 'dotted') return 'dotted'
        if (style === 'dashed') return 'dashed'
        return 'normal'
    }

    private setLineAttributes(
        ctx: CanvasRenderingContext2D,
        useStyle: boolean,
        color: ColorValue,
        lineWidth: number,
        lineType: LineTypes
    ) {
        const dotWidth = Math.max(2, lineWidth) * window.devicePixelRatio
        const effective = useStyle ? lineType : 'normal'
        if (effective === 'dotted') ctx.setLineDash([dotWidth, dotWidth])
        else if (effective === 'dashed') ctx.setLineDash([4 * dotWidth, 2 * dotWidth])
        else ctx.setLineDash([])
        ctx.globalAlpha = color.opacity
        ctx.lineWidth = lineWidth * window.devicePixelRatio
        ctx.strokeStyle = color.color
    }

    private getPatternStatus(pattern: ChartPattern): ChartPatternTargetStatus {
        const anchorId = pattern.targetAnchorPointId ?? pattern.points[pattern.points.length - 1]?.id
        const anchor = pattern.points.find((point) => point.id === anchorId)
        const anchorTime = (anchor?.x as number) ?? 0
        const candles = this._chart.getRenderedPrimaryData()
            .filter((candle) => (candle.time as number) >= anchorTime)
            .slice(0, 150)
            .map((candle) => ({
                time: candle.time,
                close: candle.close as number,
                low: candle.low as number,
                high: candle.high as number,
            }))
        return getChartPatternTargetStatus(pattern, candles)
    }

    private getTargetColor(status: ChartPatternTargetStatus) {
        return status === 'Reached' || status === 'Failed' ? TARGET_COLOR_REACHED : TARGET_COLOR_AWAITING
    }

    private getPatternTargets(pattern: ChartPattern): number[] {
        const resolved = resolveChartPatternOverlaySettings(this._settings as Record<string, unknown>)
        if (!resolved.showPriceTarget) return []
        const targets: number[] = []
        if (pattern.priceTargetUp != null && Number.isFinite(pattern.priceTargetUp)) {
            targets.push(pattern.priceTargetUp)
        }
        if (pattern.priceTargetDown != null && Number.isFinite(pattern.priceTargetDown)) {
            targets.push(pattern.priceTargetDown)
        }
        if (!targets.length && pattern.priceTarget != null && Number.isFinite(pattern.priceTarget)) {
            targets.push(pattern.priceTarget)
        }
        return targets
    }

    private getDoublePatternTargetAnchor(
        pattern: ChartPattern,
        coords: Record<string, PointCoordinates>,
    ): PointCoordinates | undefined {
        const anchorId = pattern.targetAnchorPointId ?? pattern.points[pattern.points.length - 1]?.id ?? '5'
        const breakout = coords[anchorId]
        if (!breakout || !this.isDoubleTopBottomPattern(pattern)) return breakout

        const neck = pattern.points.find((p) => p.id === '3')
        if (!neck) return breakout
        const neckY = this.priceToCoordinate(neck.y)
        if (neckY == null) return breakout
        return {
            x: breakout.x,
            y: scaledCoordinate(neckY, this.verticalPixelRatio),
        }
    }

    private getWedgeTargetAnchor(
        pattern: ChartPattern,
        coords: Record<string, PointCoordinates>,
    ): PointCoordinates | undefined {
        const anchorId = pattern.targetAnchorPointId ?? 'BR'
        const breakout = coords[anchorId] ?? coords['B']
        if (!breakout || !this.isWedgePattern(pattern)) return breakout
        return breakout
    }

    private getPennantTargetAnchor(
        pattern: ChartPattern,
        coords: Record<string, PointCoordinates>,
    ): PointCoordinates | undefined {
        if (!this.isPennantPattern(pattern)) return undefined
        const isBearish = pattern.kind === 'BEARISH_PENNANT'
        const fallbackId = isBearish ? '6' : '2'
        const anchorId = pattern.targetAnchorPointId ?? fallbackId
        const breakout = coords[anchorId] ?? coords['B']
        if (!breakout) return undefined

        const anchorPoint = pattern.points.find((p) => p.id === anchorId)
        if (!anchorPoint) return breakout

        if (isBearish) {
            const lowerStart = pattern.points.find((p) => p.id === '4')
            const lowerEnd = pattern.points.find((p) => p.id === '6')
            if (!lowerStart || !lowerEnd) return breakout
            const lowerPrice = this.getPatternLinePriceAtTime(lowerStart, lowerEnd, anchorPoint.x)
            const lowerY = this.priceToCoordinate(lowerPrice)
            if (lowerY == null) return breakout
            return { x: breakout.x, y: scaledCoordinate(lowerY, this.verticalPixelRatio) }
        }

        const highY = this.priceToCoordinate(anchorPoint.y)
        if (highY == null) return breakout
        return { x: breakout.x, y: scaledCoordinate(highY, this.verticalPixelRatio) }
    }

    private getPatternLinePriceAtTime(from: Point, to: Point, atTime: Time): number {
        const t1 = from.x as number
        const t2 = to.x as number
        const t = atTime as number
        if (t2 === t1) return from.y
        return from.y + (to.y - from.y) * (t - t1) / (t2 - t1)
    }

    private drawTargets(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        coords: Record<string, PointCoordinates>,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ) {
        const targets = this.getPatternTargets(pattern)
        if (!targets.length) return

        const breakout = this.isWedgePattern(pattern)
            ? this.getWedgeTargetAnchor(pattern, coords)
            : this.isPennantPattern(pattern)
                ? this.getPennantTargetAnchor(pattern, coords)
                : this.isHeadAndShouldersPattern(pattern)
                    ? coords['g'] ?? coords['e']
                    : this.getDoublePatternTargetAnchor(pattern, coords)
        if (!breakout) return

        const startX = breakout.x

        targets.forEach((targetPrice) => {
            const status = this.getPatternStatusForTarget(pattern, targetPrice)
            if (!shouldDrawPriceTarget(this._settings.priceTargetsFilter, status)) return
            const endX = this.getPatternTargetEndScaledX(pattern, breakout.x, targetPrice)
            this.drawTargetLine(
                ctx,
                pattern,
                breakout,
                startX,
                endX,
                targetPrice,
                style,
            )
        })
    }

    private getPatternLineColor(
        pattern: ChartPattern,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ): string {
        return pattern.priceLineSegments?.[0]?.color
            ?? pattern.boundaryLines?.[0]?.color
            ?? style.priceLineColor.color
    }

    private getTargetBadgeColor(pattern: ChartPattern, status: ChartPatternTargetStatus): string {
        if (status === 'Reached' || status === 'Failed') return TARGET_COLOR_REACHED
        if (pattern.kind === 'DOUBLE_BOTTOM') return TV_DOUBLE_BOTTOM_TARGET_COLOR_AWAITING
        if (pattern.kind === 'DOUBLE_TOP') return TV_DOUBLE_TOP_TARGET_COLOR_AWAITING
        if (pattern.kind === 'RISING_WEDGE') return '#F23645'
        if (pattern.kind === 'BULLISH_PENNANT') return '#F23645'
        if (pattern.kind === 'BEARISH_PENNANT') return TV_DOUBLE_BOTTOM_TARGET_COLOR_AWAITING
        if (pattern.kind === 'HEAD_AND_SHOULDERS') return TARGET_COLOR_AWAITING
        return TARGET_COLOR_AWAITING
    }

    private drawTargetLine(
        ctx: CanvasRenderingContext2D,
        pattern: ChartPattern,
        anchor: PointCoordinates,
        startX: number,
        endX: number,
        targetPrice: number,
        style: ReturnType<ChartPatternOverlayTool['getResolvedStyle']>,
    ) {
        const targetY = this.priceToCoordinate(targetPrice)
        if (targetY == null) return
        const scaledTargetY = scaledCoordinate(targetY, this.verticalPixelRatio)
        const isWedge = this.isWedgePattern(pattern)
        const isDoubleTopBottom = this.isDoubleTopBottomPattern(pattern)
        const isTriangle = this.isTrianglePattern(pattern)
        const usesMeasuredMoveGuide = isWedge || isDoubleTopBottom || this.isPennantPattern(pattern) || this.isHeadAndShouldersPattern(pattern)

        ctx.beginPath()
        const patternLineColor = this.getPatternLineColor(pattern, style)
        const targetColor = usesMeasuredMoveGuide
            ? { color: patternLineColor, opacity: style.priceLineColor.opacity }
            : style.neckLineColor
        const targetLineWidth = usesMeasuredMoveGuide ? style.priceLineWidth : style.neckLineWidth
        const targetLineType = usesMeasuredMoveGuide || isTriangle ? 'dotted' as LineTypes : style.neckLineStyle
        this.setLineAttributes(ctx, true, targetColor, targetLineWidth, targetLineType)

        if (usesMeasuredMoveGuide) {
            const status = this.getPatternStatusForTarget(pattern, targetPrice)
            const horizontalEnd = endX

            ctx.moveTo(anchor.x, anchor.y)
            ctx.lineTo(anchor.x, scaledTargetY)
            ctx.moveTo(anchor.x, scaledTargetY)
            ctx.lineTo(horizontalEnd, scaledTargetY)
            ctx.stroke()
            this.recordLineHit(anchor, { x: anchor.x, y: scaledTargetY })
            this.recordLineHit({ x: anchor.x, y: scaledTargetY }, { x: horizontalEnd, y: scaledTargetY })

            const badgeColor = this.getTargetBadgeColor(pattern, status)
            const layout = this.drawTargetBadge(ctx, horizontalEnd, scaledTargetY, badgeColor)
            if (layout) {
                this.targetHitAreas.push({
                    bounds: layout.bounds,
                    pattern,
                    targetPrice,
                    tooltipAnchorX: layout.tooltipAnchorX,
                    tooltipAnchorY: layout.tooltipAnchorY,
                })
            }
            return
        }

        ctx.moveTo(startX, scaledTargetY)
        ctx.lineTo(endX, scaledTargetY)
        ctx.stroke()
        this.recordLineHit({ x: startX, y: scaledTargetY }, { x: endX, y: scaledTargetY })

        const status = this.getPatternStatusForTarget(pattern, targetPrice)
        const layout = this.drawTargetBadge(ctx, endX, scaledTargetY, this.getTargetBadgeColor(pattern, status))
        if (layout) {
            this.targetHitAreas.push({
                bounds: layout.bounds,
                pattern,
                targetPrice,
                tooltipAnchorX: layout.tooltipAnchorX,
                tooltipAnchorY: layout.tooltipAnchorY,
            })
        }
    }

    private getPatternStatusForTarget(pattern: ChartPattern, targetPrice: number): ChartPatternTargetStatus {
        const anchorId = pattern.targetAnchorPointId ?? pattern.points[pattern.points.length - 1]?.id
        const anchor = pattern.points.find((point) => point.id === anchorId)
        const anchorTime = (anchor?.x as number) ?? 0
        const candles = this._chart.getRenderedPrimaryData()
            .filter((candle) => (candle.time as number) >= anchorTime)
            .slice(0, 150)
            .map((candle) => ({
                time: candle.time,
                close: candle.close as number,
                low: candle.low as number,
                high: candle.high as number,
            }))
        return getChartPatternTargetStatus({ ...pattern, priceTarget: targetPrice }, candles)
    }

    private getTargetBadgeRatios() {
        return {
            horizontalPixelRatio: this.horizontalPixelRatio,
            verticalPixelRatio: this.verticalPixelRatio,
        }
    }

    private drawTargetBadge(
        ctx: CanvasRenderingContext2D,
        targetPointX: number,
        targetPointY: number,
        color: string,
    ): PatternTargetBadgeLayout | undefined {
        return drawPatternTargetBadge(ctx, targetPointX, targetPointY, color, this.getTargetBadgeRatios())
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        if (this.displayType !== DrawingDisplayType.INDICATOR) return null
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
            this.clearHoverTooltip()
            return null
        }

        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const margin = 6 * window.devicePixelRatio

        for (let i = 0; i < this.markerHitAreas.length; i++) {
            const area = this.markerHitAreas[i]
            const { bounds } = area
            if (
                scaledX >= bounds.left - margin &&
                scaledX <= bounds.right + margin &&
                scaledY >= bounds.top - margin &&
                scaledY <= bounds.bottom + margin
            ) {
                const key = `${area.pattern.kind}-${area.pattern.points[0]?.x}`
                if (this._hoveredMarkerKey !== key) {
                    this._hoveredMarkerKey = key
                    this._markerHovered = true
                    const anchor = area.pattern.points[0]
                    const label = area.pattern.labels?.[0]?.text
                        ?? this._settings.patternTitle
                        ?? area.pattern.kind.replace(/_/g, ' ')
                    this._chart.publishEvent(ChartEvents.DRAWING_HOVERED, {
                        type: 'chartPatternOverlay',
                        time: anchor?.x,
                        price: area.anchorPrice,
                        anchorOffsetY: area.tooltipAnchorOffsetY,
                        title: label,
                        description: area.pattern.description,
                        tooltipMode: 'candlestick',
                    })
                }
                return { externalId: `${this.id}__marker-${i}`, zOrder: 'top', cursorStyle: 'default' }
            }
        }

        for (let i = 0; i < this.targetHitAreas.length; i++) {
            const area = this.targetHitAreas[i]
            const { bounds } = area
            if (
                scaledX >= bounds.left - margin &&
                scaledX <= bounds.right + margin &&
                scaledY >= bounds.top - margin &&
                scaledY <= bounds.bottom + margin
            ) {
                const key = `${area.pattern.kind}-${area.pattern.points[0]?.x}-${area.targetPrice}`
                if (this._hoveredTargetKey !== key) {
                    this._hoveredTargetKey = key
                    this._targetHovered = true
                    const anchorId = area.pattern.targetAnchorPointId ?? area.pattern.points[area.pattern.points.length - 1]?.id
                    const anchor = area.pattern.points.find((p) => p.id === anchorId)
                    this._chart.publishEvent(ChartEvents.DRAWING_HOVERED, {
                        type: 'chartPatternOverlay',
                        time: anchor?.x,
                        price: area.targetPrice,
                        title: this._settings.patternTitle ?? area.pattern.kind.replace(/_/g, ' '),
                        status: this.getPatternStatusForTarget(area.pattern, area.targetPrice),
                        tooltipMode: 'targetLabel',
                        anchorScreenX: area.tooltipAnchorX / this.horizontalPixelRatio,
                        anchorScreenY: area.tooltipAnchorY / this.verticalPixelRatio,
                    })
                }
                return { externalId: `${this.id}__target-${i}`, zOrder: 'top', cursorStyle: 'pointer' }
            }
        }

        for (let i = 0; i < this.labelHitAreas.length; i++) {
            if (this.isPointInBounds(scaledX, scaledY, this.labelHitAreas[i].bounds, margin)) {
                return { externalId: `${this.id}__label-${i}`, zOrder: 'top', cursorStyle: 'pointer' }
            }
        }

        const lineThreshold = 8 * window.devicePixelRatio
        for (let i = 0; i < this.lineHitAreas.length; i++) {
            const { p1, p2 } = this.lineHitAreas[i]
            if (getDistanceFromSegment(scaledX, scaledY, p1.x, p1.y, p2.x, p2.y) <= lineThreshold) {
                return { externalId: `${this.id}__line-${i}`, zOrder: 'top', cursorStyle: 'pointer' }
            }
        }

        this.clearHoverTooltip()
        return null
    }

    private clearHoverTooltip() {
        if (!this._targetHovered && !this._markerHovered) return
        this._targetHovered = false
        this._markerHovered = false
        this._hoveredTargetKey = ''
        this._hoveredMarkerKey = ''
        this._chart.publishEvent(ChartEvents.DRAWING_HOVERED, null)
    }

    private clearTargetTooltip() {
        this.clearHoverTooltip()
    }

    getAutoscaleInfo(startTimePoint: Logical, endTimePoint: Logical): AutoscaleInfo | null {
        if (this.displayType !== DrawingDisplayType.INDICATOR) return null

        const patterns = [...this.getCandlestickPatterns(), ...this.getChartPatterns()]
        if (!patterns.length) return null

        const prices: number[] = []
        patterns.forEach((pattern) => {
            const times = pattern.points.map((point) => point.x)
            if (!isTimeRangeOverlappingVisibleLogicalRange(
                times,
                (time) => this.timeToLogical(time),
                startTimePoint,
                endTimePoint,
            )) {
                return
            }

            pattern.points.forEach((point) => {
                if (Number.isFinite(point.y)) {
                    prices.push(point.y)
                }
            })

            this.getPatternTargets(pattern).forEach((target) => {
                if (Number.isFinite(target)) {
                    prices.push(target)
                }
            })
        })

        return buildAutoscaleInfoFromPrices(prices)
    }

    onSettingsUpdate = (settings: Partial<ChartPatternOverlaySettings>) => {
        if (settings.patterns) {
            this._settings.patterns = settings.patterns
        }
        if (settings.patternTitle) {
            this._settings.patternTitle = settings.patternTitle
        }
        if (settings.seriesGroupId) {
            this._settings.seriesGroupId = settings.seriesGroupId
        }
    }

    getDrawingType(): DrawingTypes {
        return 'chartPatternOverlay'
    }

    getSettingsValues(): ChartPatternOverlaySettings {
        return { ...this._settings }
    }

    getAvailableSettings = () => []
    getMiniSettings = () => [] as (keyof ChartPatternOverlaySettings)[]
    getHandles = () => []
}

export default ChartPatternOverlayTool
